import React, { useEffect } from 'react';
import {
  cleanUpVtkRenderer,
  initializeVtkRenderer,
  setContainer,
  unregisterManualCprLabelSyncTarget,
} from '../Renderers/vtkManagers';
import type { ContextKey, CPRContext } from '../Renderers/vtkManagers';

export function useCprRendererLifecycle({
  ref,
  context,
  rendererId,
  analysisType,
  contextPool,
  setContextPool,
  disabled = false,
}: {
  ref: React.RefObject<HTMLDivElement>;
  context: React.MutableRefObject<CPRContext | null>;
  rendererId: string;
  analysisType: string;
  contextPool: Partial<Record<ContextKey, CPRContext | null>>;
  setContextPool: React.Dispatch<React.SetStateAction<any>>;
  disabled?: boolean;
}) {
  const pooledContext = contextPool[rendererId as ContextKey];

  useEffect(() => {
    if (disabled) {
      const existing = pooledContext;
      if (existing) {
        const existingRef = { current: existing } as React.MutableRefObject<
          CPRContext | null
        >;
        unregisterManualCprLabelSyncTarget(rendererId);
        cleanUpVtkRenderer(existingRef, existing);
      }
      setContextPool((prev: any) => ({ ...prev, [rendererId]: null }));
      context.current = null;
      return;
    }
    if (!ref.current) return;
    if (!rendererId) return;

    let cntx = pooledContext;
    if (!cntx) {
      cntx = initializeVtkRenderer(rendererId, 'CPR') as CPRContext;
      setContextPool((prev: any) => ({ ...prev, [rendererId]: cntx }));
    }

    if (!cntx) throw new Error('Failed to initialize CPR renderer');
    setContainer(cntx, ref);
    context.current = cntx;

    return () => {
      unregisterManualCprLabelSyncTarget(rendererId);
      cleanUpVtkRenderer(context, cntx);
      setContextPool((prev: any) => ({ ...prev, [rendererId]: cntx }));
    };
  }, [ref, analysisType, rendererId, disabled, pooledContext]);
}
